import React from 'react';
import { RefreshCw, Route as RouteIcon, CalendarClock, Group, ChevronDown, LayoutDashboard } from 'lucide-react';
import { TECHNICIAN_PREVISAO_SESSIONS } from '../constants/technicianMenuConstants';
import { hasGoAppLicense } from '../../../utils/userFinanceiroAccess';
import '../../experiencia-cliente/components/ExperienciaClienteDetalhesModal.css';
import './TechnicianMenuModals.css';

function closeMenuForTechnician(technician, setShowTechnicianMenu, setTechnicianMenuPrevisaoOpen) {
  setShowTechnicianMenu((prev) => ({ ...prev, [technician]: false }));
  setTechnicianMenuPrevisaoOpen(null);
}

const GO_PREMIUM_ID_PRODUTO = 4;
/** Ambiente de desenvolvimento — atalho “Roteiro Old”. */
const DEV_CLIENT_ID = 1;

/** Novo painel: App Go + licença Premium (id_produto = 4). */
export function canShowNovoPainelMenuItem(user) {
  return hasGoAppLicense(user) && Number(user?.go_id_produto) === GO_PREMIUM_ID_PRODUTO;
}

function canShowRoteiroOldMenuItem(user) {
  return Number(user?.clientId) === DEV_CLIENT_ID;
}

export function TechnicianMenuModals({
  showTechnicianMenu = {},
  technicianMenuPositions = {},
  technicianMenuPrevisaoOpen = null,
  setShowTechnicianMenu,
  setTechnicianMenuPrevisaoOpen,
  technicianFilters = {},
  toggleTechnicianFilter,
  onRefreshTechnician,
  onViewTechnicianRoute,
  onOpenTodayRoute,
  onOpenNovoPainel,
  user = null,
}) {
  const showNovoPainel = canShowNovoPainelMenuItem(user);
  const showRoteiroOld = canShowRoteiroOldMenuItem(user);

  return (
    <>
      {Object.entries(showTechnicianMenu).map(([technician, isOpen]) => {
        if (!isOpen || !technicianMenuPositions[technician]) return null;

        const position = technicianMenuPositions[technician];
        const isPrevisaoOpen = technicianMenuPrevisaoOpen === technician;

        return (
          <div
            key={technician}
            className="technician-menu-modal"
            style={{
              position: 'fixed',
              top: `${position.top}px`,
              left: `${position.left}px`,
              zIndex: 99999,
            }}
          >
            <div
              className="technician-menu-dropdown ec-actions-menu"
              role="menu"
              aria-label={`Opções de ${technician}`}
            >
              <div className="technician-menu-header">
                <span className="technician-menu-header-name">{technician}</span>
              </div>

              <button
                type="button"
                className="ec-actions-menu-item"
                role="menuitem"
                onClick={() => {
                  closeMenuForTechnician(technician, setShowTechnicianMenu, setTechnicianMenuPrevisaoOpen);
                  onRefreshTechnician?.(technician);
                }}
              >
                <RefreshCw size={16} strokeWidth={1.5} aria-hidden />
                Atualizar
              </button>

              <button
                type="button"
                className="ec-actions-menu-item"
                role="menuitem"
                onClick={() => {
                  closeMenuForTechnician(technician, setShowTechnicianMenu, setTechnicianMenuPrevisaoOpen);
                  if (showNovoPainel) {
                    // Premium: novo painel → LiveMap → Roteirizar
                    onOpenNovoPainel?.(technician, { tab: 'map', mapMode: 'plan' });
                  } else {
                    onViewTechnicianRoute?.(technician);
                  }
                }}
              >
                <RouteIcon size={16} strokeWidth={1.5} aria-hidden />
                Roteirizar ordens de serviço
              </button>

              {showRoteiroOld ? (
                <button
                  type="button"
                  className="ec-actions-menu-item"
                  role="menuitem"
                  onClick={() => {
                    closeMenuForTechnician(technician, setShowTechnicianMenu, setTechnicianMenuPrevisaoOpen);
                    // Sempre o fluxo legado (RouteModal), mesmo com Premium Go
                    onViewTechnicianRoute?.(technician);
                  }}
                >
                  <RouteIcon size={16} strokeWidth={1.5} aria-hidden />
                  Roteiro Old
                </button>
              ) : null}

              <button
                type="button"
                className="ec-actions-menu-item"
                role="menuitem"
                onClick={() => {
                  closeMenuForTechnician(technician, setShowTechnicianMenu, setTechnicianMenuPrevisaoOpen);
                  if (showNovoPainel) {
                    // Premium: novo painel → Produtividade
                    onOpenNovoPainel?.(technician, { tab: 'prod' });
                  } else {
                    onOpenTodayRoute?.(technician);
                  }
                }}
              >
                <CalendarClock size={16} strokeWidth={1.5} aria-hidden />
                Roteiro de hoje
              </button>

              {showNovoPainel ? (
              <button
                type="button"
                className="ec-actions-menu-item"
                role="menuitem"
                onClick={() => {
                  closeMenuForTechnician(technician, setShowTechnicianMenu, setTechnicianMenuPrevisaoOpen);
                  onOpenNovoPainel?.(technician);
                }}
              >
                <LayoutDashboard size={16} strokeWidth={1.5} aria-hidden />
                Novo painel
              </button>
              ) : null}

              <button
                type="button"
                className={`ec-actions-menu-item technician-menu-previsao-trigger${isPrevisaoOpen ? ' is-active' : ''}`}
                role="menuitem"
                aria-expanded={isPrevisaoOpen}
                onClick={(e) => {
                  e.stopPropagation();
                  setTechnicianMenuPrevisaoOpen((prev) => (prev === technician ? null : technician));
                }}
              >
                <Group size={16} strokeWidth={1.5} aria-hidden />
                <span className="technician-menu-previsao-label">Previsão</span>
                <ChevronDown
                  size={14}
                  strokeWidth={1.5}
                  className="technician-menu-previsao-chevron"
                  aria-hidden
                />
              </button>

              {isPrevisaoOpen && (
                <div
                  className="technician-menu-previsao-panel"
                  onClick={(e) => e.stopPropagation()}
                >
                  {TECHNICIAN_PREVISAO_SESSIONS.map((groupName) => (
                    <div key={groupName} className="technician-menu-previsao-row">
                      <span className="technician-menu-previsao-row-label">{groupName}</span>
                      <label className="condicoes-install-toggle">
                        <input
                          type="checkbox"
                          checked={technicianFilters[technician]?.[groupName] || false}
                          onChange={() => toggleTechnicianFilter?.(technician, groupName)}
                        />
                        <span className="condicoes-install-pill">
                          <span className="condicoes-install-dot" />
                        </span>
                      </label>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        );
      })}
    </>
  );
}
